/**
 * PositionHero — Phase 86. The single source of truth on screen.
 *
 * Replaces the two competing summaries that used to sit on /accounts: a hero
 * that summed the browser's account list and a KPI row that trusted a separate
 * backend calculation. They disagreed by ~₹2L. Everything here now comes from
 * one `/api/position` response, so the numbers cannot drift apart.
 *
 * Answers the four questions the old page couldn't:
 *   • What do I own and owe?          → net worth / assets / liabilities
 *   • How much is genuinely available? → available cash, flagged when the
 *                                        underlying balances aren't trustworthy
 *   • How current is this?             → freshness summary + FX as-of
 *   • What went into it?               → "See what's included"
 */
import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, LineChart } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { getLastPositionError } from '@/lib/positionApi';
import PositionDisclosure from './PositionDisclosure';

function fmtDay(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch { return iso; }
}

function fmtMonthYear(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch { return iso; }
}

function TrendTooltip({ active, payload, label, base }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-sm">
      <p className="text-[11px] text-muted-foreground mb-0.5">{fmtDay(label)}</p>
      <p className="num text-sm font-semibold text-popover-foreground">
        {formatMoney(payload[0].value, base)}
      </p>
    </div>
  );
}

function Metric({ label, value, tone, testid, note }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest briefing-hero__muted font-medium mb-1.5">
        {label}
      </p>
      <p className="num font-display text-xl font-medium" style={tone ? { color: tone } : undefined}
         data-testid={testid}>
        {value}
      </p>
      {note && (
        <p className="text-[10px] briefing-hero__muted mt-1 leading-snug">{note}</p>
      )}
    </div>
  );
}

/** Net-worth trend. Recorded only — never reconstructed. */
function PositionTrend({ history, base }) {
  const points = history?.points || [];

  if (!history?.sufficient) {
    const started = history?.tracking_started;
    return (
      <div
        className="briefing-hero__panel h-full min-h-[180px] flex flex-col justify-center px-5 py-6"
        data-testid="position-trend-empty"
      >
        <LineChart className="h-4 w-4 briefing-hero__accent mb-2.5" aria-hidden="true" />
        <p className="text-sm font-medium mb-1.5">Your position trend is being recorded</p>
        <p className="text-xs briefing-hero__muted leading-relaxed">
          {started
            ? `Historical tracking started in ${fmtMonthYear(started)}. Your trend will
               appear here once there are a few points to compare.`
            : `2120 starts recording your financial position from today. A trend line
               will appear here as more snapshots are captured — we'd rather show
               nothing than draw a flat line that implies your position never moved.`}
        </p>
      </div>
    );
  }

  const first = points[0];
  const last = points[points.length - 1];
  const delta = last.net_worth - first.net_worth;

  return (
    <div data-testid="position-trend-chart">
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[10px] uppercase tracking-widest briefing-hero__muted font-semibold">
          Your financial position
        </p>
        <p className="text-[10px] briefing-hero__muted">
          {fmtDay(first.date)} – {fmtDay(last.date)} · recorded balances
        </p>
      </div>
      <div style={{ height: 180, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <AreaChart data={points} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="positionTrendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--financial-briefing-accent)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--financial-briefing-accent)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={fmtDay}
              tick={{ fontSize: 11, fill: 'var(--financial-briefing-text-muted)' }}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis hide domain={['auto', 'auto']} />
            <Tooltip content={<TrendTooltip base={base} />} />
            <Area
              type="monotone"
              dataKey="net_worth"
              stroke="var(--financial-briefing-accent)"
              strokeWidth={1.8}
              fill="url(#positionTrendGrad)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: 'var(--financial-briefing-accent)' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] briefing-hero__muted mt-2" data-testid="position-trend-delta">
        {delta === 0
          ? 'Unchanged since tracking began.'
          : `${delta > 0 ? '↑' : '↓'} ${formatMoney(Math.abs(delta), base)} since ${fmtDay(first.date)}`}
      </p>
    </div>
  );
}

export default function PositionHero({ position, history, loading, error = null }) {
  const [showDisclosure, setShowDisclosure] = useState(false);

  if (loading && !position) {
    return (
      <section className="briefing-hero p-6 lg:p-8 animate-pulse" data-testid="position-hero-loading">
        <div className="h-3 w-40 rounded bg-white/10 mb-4" />
        <div className="h-10 w-64 rounded bg-white/10 mb-6" />
        <div className="grid grid-cols-2 gap-4 max-w-md">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-12 rounded bg-white/10" />)}
        </div>
      </section>
    );
  }

  if (!position) {
    const err = error || getLastPositionError();
    return (
      <section className="briefing-hero p-6 lg:p-8" data-testid="position-hero-unavailable">
        <p className="text-sm">We couldn&rsquo;t load your financial position just now.</p>
        <p className="text-xs briefing-hero__muted mt-1">
          Your data is safe — nothing has been lost. Your accounts below are unaffected.
        </p>
        {err && (
          <p className="text-xs briefing-hero__muted mt-2 font-mono" data-testid="position-hero-error-detail">
            {err.status ? `HTTP ${err.status}` : 'No response'} · {err.detail}
          </p>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center rounded-full border border-white/25 px-3 py-1 text-xs text-white/90 hover:bg-white/10 transition-colors"
          data-testid="position-hero-retry"
        >
          Try again
        </button>
      </section>
    );
  }

  const base = position.base_currency;
  const included = position.included_resources || [];
  const stale = position.freshness?.stale_count || 0;
  const unconfirmed = position.freshness?.unconfirmed_count || 0;
  const untrustedCash = position.freshness?.untrusted_available_cash || 0;

  return (
    <>
      <section
        className="briefing-hero p-6 lg:p-8"
        data-testid="accounts-position-hero"
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: the numbers */}
          <div className="lg:col-span-2">
            <p className="text-[10px] uppercase tracking-widest briefing-hero__muted font-semibold mb-3"
               data-testid="position-hero-eyebrow">
              Net worth · {included.length} resource{included.length === 1 ? '' : 's'} included
            </p>
            <p
              className="font-display text-3xl lg:text-4xl font-medium num leading-none"
              style={{ letterSpacing: '-0.02em' }}
              data-testid="position-net-worth"
            >
              {formatMoney(position.net_worth, base)}
            </p>

            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
              <Metric
                label="Total assets"
                value={formatMoney(position.assets, base)}
                testid="position-assets"
              />
              <Metric
                label="Total liabilities"
                value={formatMoney(position.liabilities, base)}
                tone="#E39A86"
                testid="position-liabilities"
              />
              <Metric
                label="Available cash"
                value={formatMoney(position.available_cash, base)}
                testid="position-available-cash"
                note={untrustedCash > 0
                  ? `${formatMoney(untrustedCash, base)} of this sits behind a balance we can't confirm`
                  : null}
              />
              <Metric
                label="Long-term investments"
                value={formatMoney(position.invested, base)}
                tone="var(--financial-briefing-accent)"
                testid="position-invested"
              />
            </div>

            {position.other_assets > 0 && (
              <p className="text-[11px] briefing-hero__muted mt-4" data-testid="position-other-assets">
                Plus {formatMoney(position.other_assets, base)} in other assets.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-5">
              <button
                type="button"
                onClick={() => setShowDisclosure(true)}
                className="text-xs font-medium briefing-hero__accent hover:underline"
                data-testid="position-see-included"
              >
                See what&rsquo;s included →
              </button>
              <span className="text-[11px] briefing-hero__muted">
                FX as of {position.fx?.as_of_date || '—'}
              </span>
            </div>
          </div>

          {/* Right: trend, or an honest explanation of its absence */}
          <div className="lg:col-span-3">
            <PositionTrend history={history} base={base} />
          </div>
        </div>

        {stale > 0 && (
          <div
            className="briefing-hero__panel mt-6 px-4 py-3 flex items-start gap-2.5"
            data-testid="position-freshness-banner"
          >
            <AlertTriangle className="h-4 w-4 briefing-hero__accent mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-xs leading-relaxed">
              <span className="font-semibold">
                {stale} balance{stale === 1 ? '' : 's'} need{stale === 1 ? 's' : ''} review.
              </span>{' '}
              <span className="briefing-hero__muted">
                {(position.freshness.issues || []).slice(0, 2).map((i, idx) => (
                  <React.Fragment key={i.id}>
                    {idx > 0 ? '; ' : ''}{i.name} — {(i.label || '').toLowerCase()}
                  </React.Fragment>
                ))}
                {(position.freshness.issues || []).length > 2
                  ? ` and ${position.freshness.issues.length - 2} more`
                  : ''}
                .
              </span>
            </p>
          </div>
        )}

        {stale === 0 && unconfirmed > 0 && (
          <p className="text-[11px] briefing-hero__muted mt-5" data-testid="position-unconfirmed-note">
            {unconfirmed} balance{unconfirmed === 1 ? '' : 's'} {unconfirmed === 1 ? 'has' : 'have'} never
            been confirmed, so these totals are only as current as the last figure you entered.
          </p>
        )}
      </section>

      <PositionDisclosure
        open={showDisclosure}
        onOpenChange={setShowDisclosure}
        position={position}
      />
    </>
  );
}
